import { exceptionLogger } from '@paradigm/logging/src/logging';

import { echo } from '#/shared';

import createWebSocket, {
  ApiWebSocket,
  CreateWebSocketOptions,
  WSNotification,
} from './createWebSocket';

/**
 * Same as `createWebSocket` but with extra handlers especificallly
 * for PAPI web sockets.
 *
 * @see {@link createWebSocket}
 */
export default function createPapiWebSocket<
  RawNotification extends WSNotification = WSNotification,
  Notification = WSNotification,
>(
  options: CreateWebSocketOptions<RawNotification, Notification>,
): ApiWebSocket<Notification> {
  const ws = createWebSocket(options);

  ws.addEventListener('error', testTokenValidity);

  return ws;
}

/**
 * Minimum interval between token-validity echos. Each PAPI WebSocket
 * fires its own `error` event on logout/auth change, and on a workspace
 * with many concurrent subscriptions (block-terminal side-panel on the
 * /rfqs page, for example) a single auth flip can produce dozens of
 * parallel `echo` calls. That bursts past Chrome's per-origin socket
 * limit and triggers a cascading `ERR_INSUFFICIENT_RESOURCES` storm —
 * the very condition the echo was meant to recover from.
 *
 * 2 seconds covers the realistic burst window (handshake retries,
 * coordinated reconnects) without delaying a real token-rotation check.
 */
const ECHO_MIN_INTERVAL_MS = 2_000;

/**
 * Factory so tests can construct an isolated debouncer with its own
 * state, avoiding module-level singletons in test code.
 */
export function makeTestTokenValidity(minIntervalMs = ECHO_MIN_INTERVAL_MS) {
  let lastEchoAt = 0;
  let inFlight = false;

  return function testTokenValidity() {
    if (inFlight) return;
    const now = Date.now();
    if (now - lastEchoAt < minIntervalMs) return;
    lastEchoAt = now;
    inFlight = true;

    async function test() {
      /*
       * We don't need to do handle the response, if the http request
       * fails with 401 or 403 (invalid token), there'll be a message
       * published on the token expiration topic. The application
       * listening to the token expiration topic will handle it,
       * probably by signing out the user.
       */
      try {
        await echo({});
      } finally {
        inFlight = false;
      }
    }

    test().catch(exceptionLogger('Failed testing PAPI token validity'));
  };
}

const testTokenValidity = makeTestTokenValidity();
